import { z } from 'zod';
import { dayOf } from './deadline.js';
/**
 * **La date de rendez-vous et la date de réalisation sont deux dates distinctes** (#115, remonté
 * par le test terrain du 25/08). Un contrôle se fait souvent un autre jour que celui du
 * rendez-vous pris — report, absence, réorganisation de tournée — et rien ne le disait :
 * l'agenda portait `Appointment.scheduled_at`, la fiche transmise ne datait que sa clôture sur la
 * tablette (`submitted_at`), qui n'est ni l'une ni l'autre.
 *
 * ⚠️ **On ne réaligne jamais les deux** (M21). Le seul geste autorisé devant l'écart est de le
 * montrer : c'est la consigne centrale de l'issue. Corriger en silence effacerait le fait qu'un
 * contrôle a glissé, qui est justement ce que le bureau doit voir.
 */
/**
 * Un **jour du calendrier** (`2026-08-05`), pas un instant — d'où `_on` et non `_at`.
 *
 * ⚠️ La distinction n'est pas cosmétique. Ce que l'agent sait, c'est le *jour* du passage ; lui
 * demander une heure inventerait une précision qu'il n'a pas, et un instant sans fuseau se
 * décalerait d'un jour à la première lecture depuis un autre fuseau — exactement le genre d'écart
 * fantôme que cette fiche existe pour éviter.
 */
export const performedOnSchema = z.iso.date();
const DAY_MS = 86_400_000;
/**
 * L'écart entre le rendez-vous pris et le jour où le contrôle a réellement eu lieu.
 *
 * ⚠️ **Le jour du rendez-vous se lit à l'heure de Paris** (`dayOf`) : ni en UTC, qui ferait
 * basculer un rendez-vous de 0 h 30 à la veille, ni au fuseau de l'appareil.
 *
 * ⚠️ Une valeur illisible vaut « absente » plutôt que de lever : ces deux dates viennent d'un
 * JSONB que la base ne valide pas et d'un cache de tablette qui peut précéder ce champ. Une fiche
 * doit rester lisible (M21).
 */
export function appointmentDeviation(scheduledAt, performedOn) {
    const performed = performedDay(performedOn);
    // ⚠️ Le jour manquant se teste **avant** l'absence de rendez-vous, et l'ordre est tout le
    // propos : la plupart des demandes n'en portent pas encore, et l'ordre inverse laissait
    // justement celles-là partir sans que rien n'ait réclamé le jour du passage (M21).
    if (performed === null)
        return { kind: 'undated' };
    const scheduled = scheduledDay(scheduledAt);
    if (scheduled === null)
        return { kind: 'no_appointment' };
    const days = Math.round((performed - scheduled) / DAY_MS);
    return days === 0 ? { kind: 'as_planned' } : { kind: 'shifted', days };
}
/**
 * Le jour déclaré, **à minuit dans le fuseau du lecteur** — `null` dès que la chaîne n'est pas un
 * jour du calendrier valide.
 *
 * C'est la **seule** porte d'entrée : les deux fronts s'en étaient chacun refait une, et elles
 * affichaient comme certaine une date que le reste du code tenait pour absente.
 *
 * ⚠️ La garde est le schéma, jamais un test d'`undefined` : `'a-b-c'` rend trois `NaN` que le
 * constructeur `Date` avale sans broncher — l'écran imprime alors « Invalid Date » — et qu'`Intl`
 * refuse en **levant**, emportant la vue entière.
 *
 * ⚠️ `T00:00:00` n'est pas décoratif, et le constructeur à trois nombres est proscrit :
 * `new Date('2026-08-06')` — forme date seule — est lu comme minuit **UTC**, donc comme la veille
 * à l'ouest de Greenwich ; et `new Date(26, 7, 6)` remappe les années 0–99 sur 1900–1999, quand
 * un `<input type="date">` laisse très bien saisir `0026-08-06`.
 */
export function calendarDay(day) {
    const parsed = performedOnSchema.safeParse(day);
    if (!parsed.success)
        return null;
    return new Date(`${parsed.data}T00:00:00`);
}
/**
 * L'écart en toutes lettres — jamais « J+2 », qui se lit vite mais se comprend mal quand on ouvre
 * la fiche trois semaines plus tard.
 *
 * `null` quand il n'y a rien à signaler : pas de rendez-vous à comparer, ou contrôle fait le jour
 * dit. Un encart sur chaque fiche cesserait d'être lu au bout de trois.
 *
 * ⚠️ La phrase dit ce qui s'est passé, jamais ce qu'il faut corriger. Un contrôle décalé est banal
 * — report, absence, réorganisation de tournée — et le gestionnaire est seul juge de ce qu'il en
 * fait (M21).
 *
 * ⚠️ Elle vit dans le domaine et non dans un écran : les deux fronts disent le même écart, et
 * deux copies avaient déjà divergé d'un mot à la première écriture.
 */
export function describeAppointmentDeviation(deviation) {
    if (deviation.kind !== 'shifted')
        return null;
    const count = Math.abs(deviation.days);
    const plural = count > 1 ? 's' : '';
    return deviation.days > 0
        ? `Contrôle réalisé ${count} jour${plural} après le rendez-vous pris.`
        : `Contrôle réalisé ${count} jour${plural} avant le rendez-vous pris.`;
}
/**
 * Le jour du calendrier d'une date, dans le fuseau du lecteur — la forme que
 * {@link performedOnSchema} accepte, et celle qu'un `<input type="date">` échange.
 *
 * ⚠️ Pas de `toISOString().slice(0, 10)` : celui-là rend le jour **UTC**, donc la veille pour
 * toute soirée d'été française.
 */
export function toCalendarDay(date) {
    const year = String(date.getFullYear()).padStart(4, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
/**
 * Minuit du jour, en repère UTC : deux jours ainsi ramenés se soustraient sans dérive.
 *
 * ⚠️ Par la chaîne et non par `Date.UTC(year, …)`, qui remappe les années 0–99 sur 1900–1999 :
 * `0026-08-06` y devenait 1926, dans une fiche dont toute la doctrine est « on n'invente pas une
 * date ».
 */
function utcDay(day) {
    return Date.parse(`${day}T00:00:00Z`);
}
function scheduledDay(scheduledAt) {
    if (scheduledAt === null || scheduledAt === undefined)
        return null;
    const parsed = scheduledAt instanceof Date ? scheduledAt : new Date(scheduledAt);
    if (Number.isNaN(parsed.getTime()))
        return null;
    return utcDay(dayOf(parsed));
}
function performedDay(performedOn) {
    const parsed = performedOnSchema.safeParse(performedOn);
    return parsed.success ? utcDay(parsed.data) : null;
}
//# sourceMappingURL=inspection-date.js.map